/**
 * MochiPet3D — reusable Mochi companion with an imperative controller API.
 *
 * WHERE THE EXPRESSION LOGIC LIVES:
 *  - Per-mood face/ears/mouth/tail/body targets: `moodTargets()` in ./MochiCanvas.jsx
 *  - Per-frame easing, blink, bounce, head-droop, ±15° look/drag: `MochiModel` useFrame in ./MochiCanvas.jsx
 *  - This file only orchestrates WHICH mood is active + one-shot interactions + speech.
 *
 * Moods (rig-spec aligned): happy | excited | hungry | sleepy | tired | sick | sad | dead | revive | eat | pet_reaction
 * Controller API (via ref): setMood, pet, feed, play, sleep, heal, revive, setLookOffset (clamped ±15°)
 */
import React, { forwardRef, useImperativeHandle, useState, useRef, useCallback, useEffect } from "react";
import MochiReal from "./MochiReal";
import { SFX } from "../../utils/mochiSound";

const SPEECH = {
  happy: ["Dekat kalian rasanya nyaman.", "Hari ini menyenangkan ya."],
  excited: ["Yeay! Seru banget!", "Wii, ayo main lagi!"],
  hungry: ["Perutku keroncongan…", "Ada cemilan nggak?"],
  sleepy: ["Zzz… ngantuk…", "Mau bobo dulu ya…"],
  tired: ["Capek, pengen rebahan.", "Energiku menipis…"],
  sick: ["Badanku nggak enak…", "Aku butuh obat."],
  sad: ["Aku kangen diajak main…", "Peluk dong…"],
  dead: ["…", "(butuh dibangkitkan)"],
  revive: ["Aku kembali! ✨", "Terima kasih sudah menolongku!"],
  eat: ["Nyam nyam… enak!", "Hmm lezat~"],
  pet_reaction: ["Prrr… enak banget!", "Hehe geli~"],
};
const pick = (m) => { const a = SPEECH[m] || SPEECH.happy; return a[Math.floor(Math.random() * a.length)]; };
const clamp15 = (v) => Math.max(-15, Math.min(15, v || 0));

const MochiPet3D = forwardRef(function MochiPet3D(
  { initialMood = "happy", accessory = "bow", theme, showSpeech = true, onMoodChange },
  ref
) {
  const [base, setBase] = useState(initialMood);
  const [transient, setTransient] = useState(null);
  const [pulse, setPulse] = useState(0);
  const [look, setLook] = useState({ yaw: 0, pitch: 0 });
  const [speech, setSpeech] = useState(pick(initialMood));
  const timer = useRef(null);
  const lastTouch = useRef(0);

  const mood = transient || base;

  useEffect(() => { onMoodChange && onMoodChange(mood); }, [mood]); // eslint-disable-line

  const flash = useCallback((m, ms, then) => {
    setTransient(m);
    setSpeech(pick(m));
    setPulse((p) => p + 1);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setTransient(null); if (then) setBase(then); }, ms);
  }, []);

  useImperativeHandle(ref, () => ({
    setMood: (m) => { clearTimeout(timer.current); setTransient(null); setBase(m); setSpeech(pick(m)); },
    pet: () => { flash("pet_reaction", 1400); SFX.cuddle(); },
    feed: () => { flash("eat", 4200); SFX.feed(); },
    play: () => { flash("excited", 1800); SFX.play(); },
    sleep: () => { clearTimeout(timer.current); setTransient(null); setBase("sleepy"); setSpeech(pick("sleepy")); SFX.sleep(); },
    heal: () => { clearTimeout(timer.current); setTransient(null); setBase("happy"); setSpeech(pick("happy")); setPulse((p) => p + 1); SFX.med(); },
    revive: () => { flash("revive", 1500, "happy"); SFX.revive(); },
    setLookOffset: (yaw, pitch) => setLook({ yaw: clamp15(yaw), pitch: clamp15(pitch) }),
    getMood: () => mood,
  }), [flash, mood]);

  return (
    <div className="relative h-full w-full">
      {showSpeech && (
        <div
          className="pointer-events-none absolute left-1/2 top-6 z-10 max-w-[80%] -translate-x-1/2 rounded-2xl bg-white/90 px-4 py-2 text-center text-[13px] font-medium text-[#7a6a5f] shadow-sm backdrop-blur"
          data-testid="mochi-speech"
        >
          {mood === "dead" ? "Mochi pingsan… bangkitkan yuk." : speech}
        </div>
      )}
      <MochiReal
        mood={mood}
        accessory={accessory}
        theme={mood === "dead" || mood === "sleepy" ? null : theme}
        actionPulse={pulse}
        look={look}
        onReact={() => { setSpeech(pick(mood)); if (mood !== "dead" && Date.now() - lastTouch.current > 400) SFX.tap(); }}
        onTouch={(part) => {
          if (mood === "dead") return;
          lastTouch.current = Date.now();
          if (part === "cheek" || part === "head") { SFX.cheek(); flash("pet_reaction", 1100); setSpeech("Meong~ geli!"); }
        }}
        night={mood === "sleepy"}
      />
    </div>
  );
});

export default MochiPet3D;
